import type { RealtimeMessage } from '@atlas/types'
import { EventType } from '@atlas/types'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { ARTICLE_KEY, ARTICLE_TRANSLATIONS_KEY, ARTICLES_KEY, articleKey } from '@/hooks/useArticles'
import { FILE_WITH_TAGS_KEY, fileWithTagsKey } from '@/hooks/useAssetDetail'
import { DISCOVER_FILES_KEY, FILES_KEY } from '@/hooks/useClassification'
import { MESSAGE_KEY, MESSAGES_KEY, messageKey, TRANSLATION_GROUP_KEY } from '@/hooks/useMessages'
import { THUMBNAIL_KEY, thumbnailKey } from '@/hooks/useThumbnails'
import { WORK_ORDER_VALIDATION_ASSETS_KEY } from '@/hooks/useValidationAssets'
import { WORK_ORDER_FILES_KEY } from '@/hooks/useWorkOrderFiles'
import { WORK_ORDER_MESSAGES_KEY } from '@/hooks/useWorkOrderMessages'
import { WORK_ORDER_DETAIL_KEY } from '@/hooks/useWorkOrders'
import { WORK_ORDER_ACTIVITY_KEY } from '@/hooks/useWorkOrderTimeline'
import { subscribeRealtime } from './realtime-dispatcher'

// Turns realtime messages into react-query invalidations (ADR-0020). This is the
// default consumer of the channel: features that only need fresh data get it here
// for free and never subscribe to anything themselves.
//
// Invalidate, never write. A message reports that something CHANGED; it does not
// carry the new value, and the channel offers neither ordering nor
// at-most-once delivery. Invalidation is idempotent and order-insensitive, so a
// duplicate costs one refetch and a reordered pair still converges on server
// state — writing payload data into the cache would need guarantees the transport
// does not make.

/**
 * Query keys a single message can affect, derived from the entity it references.
 *
 * Keys are prefixes: react-query matches them against every query whose key
 * starts with the same segments, so `['thumbnail', id]` reaches that one file's
 * thumbnail without needing to know which page rendered it.
 */
type KeyResolver = (message: RealtimeMessage) => QueryKey[]

/**
 * Keys that cannot be derived from a specific entity and must be refreshed
 * wholesale — the lists a new or changed file can appear in.
 */
const BROAD_FILE_KEYS: QueryKey[] = [
	[FILES_KEY],
	[DISCOVER_FILES_KEY],
	[WORK_ORDER_FILES_KEY],
	// Deliberately the bare prefix. `entityRef` identifies the FILE, and the
	// envelope carries no work-order id, so the bridge cannot narrow this to
	// `['work-order-detail', id]`. Prefix-matching invalidates every cached
	// order's detail rather than only the affected one; acceptable because
	// react-query refetches only queries with a live observer, which is the order
	// actually on screen.
	//
	// This one key now covers the deliverables of all five types AND their
	// thumbnails, which the aggregate presigns — the per-card thumbnail query it
	// replaced was keyed `['file-thumbnail', id]`, a key this bridge never knew
	// about, so a newly generated thumbnail never refreshed from a push.
	[WORK_ORDER_DETAIL_KEY],
]

/**
 * Everything a finished validation-asset render changes.
 *
 * All bare prefixes. `entityRef` identifies the REQUEST, and the envelope carries
 * neither the work order's id nor its code, so none of these can be narrowed to
 * the affected order. Prefix-matching invalidates the cached entry for every
 * order rather than only that one; acceptable because react-query refetches only
 * queries with a live observer, which is the order actually on screen.
 *
 * The render also stores its produced PDF + mockups as classified files, so the
 * order's file and deliverable lists are as stale as the request row itself.
 */
const VALIDATION_ASSET_KEYS: QueryKey[] = [
	[WORK_ORDER_VALIDATION_ASSETS_KEY],
	[WORK_ORDER_ACTIVITY_KEY],
	[WORK_ORDER_FILES_KEY],
	[WORK_ORDER_DETAIL_KEY],
]

/**
 * Everything a finished translation changes, beyond the message itself.
 *
 * `['translation-group']` and `['work-order-messages']` are bare prefixes: the
 * former is keyed by the translation group id and the latter by the order code,
 * and `entityRef` carries only the message id. Same trade-off as the
 * validation-asset keys — only an on-screen list actually refetches.
 */
const BROAD_MESSAGE_KEYS: QueryKey[] = [[MESSAGES_KEY], [TRANSLATION_GROUP_KEY], [WORK_ORDER_MESSAGES_KEY]]

/**
 * Everything a finished article translation changes, beyond the variant itself.
 *
 * `['article-translations']` is a bare prefix: it is keyed by the article id the
 * group was READ through, which is the source on the page the author requested
 * from, while `entityRef` carries the TARGET variant's id. The two differ by
 * design, so the affected group cannot be narrowed from the envelope. Same
 * trade-off as the message keys — only a mounted query actually refetches.
 *
 * `['articles']` matters as much as the variant's own record: a target row is an
 * article, so the index and every brand read it can appear in are as stale as
 * the variant is.
 */
const BROAD_ARTICLE_KEYS: QueryKey[] = [[ARTICLES_KEY], [ARTICLE_TRANSLATIONS_KEY]]

/**
 * A file's own views: its thumbnail and its detail record.
 *
 * @param {RealtimeMessage} message - The message whose `entityRef.id` is the file id.
 * @returns {QueryKey[]} Keys scoped to that one file.
 */
function fileKeys(message: RealtimeMessage): QueryKey[] {
	const fileId = message.entityRef.id
	return [thumbnailKey(fileId), fileWithTagsKey(fileId)]
}

/**
 * Event type → the queries it invalidates.
 *
 * Only the types the channel actually carries appear here: the publisher's
 * EventBridge pattern is the allow-list, so a wider map would be dead code
 * claiming coverage it has never had. Add an entry when the publisher starts
 * forwarding a new type.
 */
const INVALIDATION_MAP: Partial<Record<EventType, KeyResolver>> = {
	// Processing finished, but not necessarily with an output: the generator
	// reports completion with `{ skipped: true }` for non-thumbnailable input.
	// Invalidating and re-reading lets the query decide, so no consumer has to
	// interpret the payload.
	// The broad list keys matter as much as the file's own: a list row renders the
	// thumbnail from the file record it already holds, so invalidating only
	// `thumbnailKey` refreshes a thumbnail nobody is looking at while the list keeps
	// showing the pre-processing row. Any surface listing files has to re-read.
	[EventType.FILE_PROCESSING_COMPLETED]: (message) => [...fileKeys(message), ...BROAD_FILE_KEYS],
	// Also invalidates: a failure can flip a file out of a pending state, and the
	// user-visible error is raised separately from the notification handler.
	[EventType.FILE_PROCESSING_FAILED]: (message) => [...fileKeys(message), ...BROAD_FILE_KEYS],
	[EventType.FILE_UPLOADED]: (message) => [...fileKeys(message), ...BROAD_FILE_KEYS],
	// A ready render and a failed one invalidate the same set: both move the
	// request out of `requested`/`rendering`, which is what the panel is waiting
	// for. Whether an output exists is the refetched request row's answer, not the
	// event type's.
	[EventType.VALIDATION_ASSET_READY]: () => VALIDATION_ASSET_KEYS,
	[EventType.VALIDATION_ASSET_FAILED]: () => VALIDATION_ASSET_KEYS,
	// `entityRef.id` is the TRANSLATED message's id, so its own record narrows;
	// the lists it appears in cannot.
	[EventType.MESSAGE_TRANSLATED]: (message) => [messageKey(message.entityRef.id), ...BROAD_MESSAGE_KEYS],
	[EventType.MESSAGE_TRANSLATION_FAILED]: (message) => [messageKey(message.entityRef.id), ...BROAD_MESSAGE_KEYS],
	// `entityRef.id` is the TARGET variant's id, so its own record narrows; the
	// group read and the lists it appears in cannot. A ready result and a failed
	// one invalidate the same set: both move the row out of a non-terminal
	// translation status, which is what the strip is waiting for, and whether
	// there is translated text is the refetched row's answer, not the type's.
	[EventType.ARTICLE_TRANSLATED]: (message) => [articleKey(message.entityRef.id), ...BROAD_ARTICLE_KEYS],
	[EventType.ARTICLE_TRANSLATION_FAILED]: (message) => [articleKey(message.entityRef.id), ...BROAD_ARTICLE_KEYS],
}

/**
 * Every key the bridge can touch, for the reconnect sweep.
 *
 * Per-entity keys are reduced to their bare prefixes (`['thumbnail']`,
 * `['file-with-tags']`, `['message']`): after a disconnect there is no record of
 * which ids were missed, so the whole family is invalidated rather than guessed
 * at.
 */
const ALL_AFFECTED_KEYS: QueryKey[] = [
	[THUMBNAIL_KEY],
	[FILE_WITH_TAGS_KEY],
	...BROAD_FILE_KEYS,
	[WORK_ORDER_VALIDATION_ASSETS_KEY],
	[WORK_ORDER_ACTIVITY_KEY],
	[MESSAGE_KEY],
	...BROAD_MESSAGE_KEYS,
	[ARTICLE_KEY],
	...BROAD_ARTICLE_KEYS,
]

/**
 * Wires the channel into a query client. Called once by `RealtimeProvider`.
 *
 * @param {QueryClient} queryClient - Cache to invalidate against.
 * @returns {() => void} Unsubscribe function, for the provider's cleanup.
 */
export function registerQueryBridge(queryClient: QueryClient): () => void {
	return subscribeRealtime({}, (message) => {
		const resolve = INVALIDATION_MAP[message.type]
		if (!resolve) return
		invalidateAll(queryClient, resolve(message))
	})
}

/**
 * Refreshes everything the channel can affect, after a gap in delivery.
 *
 * IoT does not queue for an absent subscriber and there is no replay log, so
 * messages missed while disconnected are simply gone. Re-asking the server is
 * cheaper and more reliable than any replay scheme would be, and it is what lets
 * the rest of the design skip sequence numbers and gap detection entirely.
 *
 * @param {QueryClient} queryClient - Cache to invalidate against.
 */
export function invalidateOnReconnect(queryClient: QueryClient): void {
	invalidateAll(queryClient, ALL_AFFECTED_KEYS)
}

/**
 * Invalidates a set of key prefixes.
 *
 * @param {QueryClient} queryClient - Cache to invalidate against.
 * @param {QueryKey[]} keys - Prefixes to invalidate.
 */
function invalidateAll(queryClient: QueryClient, keys: QueryKey[]): void {
	for (const queryKey of keys) pendingKeys.add(JSON.stringify(queryKey))
	if (flushTimer) return
	flushTimer = setTimeout(() => {
		flushTimer = null
		const keysToFlush = [...pendingKeys].map((serialized) => JSON.parse(serialized) as QueryKey)
		pendingKeys.clear()
		for (const queryKey of keysToFlush) void queryClient.invalidateQueries({ queryKey })
	}, COALESCE_WINDOW_MS)
}

/**
 * Trailing window over which inbound messages accumulate before one flush.
 *
 * A five-file upload emits about twenty messages (one `file.uploaded` plus one
 * `file.processing_completed` per concern, per file), and each resolves to broad
 * key prefixes — `work-order-deliverables` alone fans out to the five mounted
 * deliverable-type queries. Invalidating per message produced a sustained ~15
 * requests/second for the whole batch (396 `/files/discover` calls observed in
 * one six-minute window) to reach a cache state identical to invalidating once.
 *
 * Keys are accumulated rather than replaced, so a burst carrying different
 * prefixes still refreshes all of them — coalescing must not drop an
 * invalidation, only merge it.
 */
const COALESCE_WINDOW_MS = 300

const pendingKeys = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

/** Test-only: drop pending coalesced keys so one spec cannot leak into the next. */
export function resetQueryBridgeCoalescing(): void {
	if (flushTimer) clearTimeout(flushTimer)
	flushTimer = null
	pendingKeys.clear()
}
